/**
 * StaleFragmentResolveMenu.tsx — PLAT-10266 (Card 2, epic PLAT-10209)
 *
 * The inline resolve affordance for a STALE (amber) translated fragment in the
 * whole-story Translation Review. Opened by right-clicking a stale fragment; a
 * small context menu on the shared `contextMenuStyles` surface — the SAME idiom
 * as `SpanReplaceTrigger` — anchored above the fragment word, dismissed on
 * click-away.
 *
 * Card 2 offers "Re-translate (machine)". It is kept a small, extensible MENU
 * (not a single hard-coded button) on purpose: Card 3 adds "Replace manually"
 * as a sibling row (rendered only when the host wires `onReplaceManually`) and
 * Card 4 adds a second home in `CoveragePopover`. Review-scoped: the host
 * (`AlignedBlocksView`) only opens it when the review passes
 * `onResolveRetranslate` (whole-story host), so the clip host and the shared
 * prose views never render it (AC6 containment).
 */
import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import RefreshIcon from '@src/svgs/RefreshIcon';
import ReplaceTermIcon from '@src/svgs/ReplaceTermIcon';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useOutsideAlerter } from './useClickOutside';

// Menu footprint used for viewport clamping (min-width 180 + padding). One row
// is ~44px; the optional "Replace manually" row adds a second.
const MENU_WIDTH = 200;
const ROW_HEIGHT = 44;

export interface StaleFragmentResolveMenuProps {
  /** The stale fragment's translated word — the menu anchors above it. */
  anchorElement: HTMLElement;
  /**
   * Re-translate the fragment from the machine (an empty-instruction directed
   * re-translate, branching on override-vs-machine in the host workspace).
   */
  onReTranslate: () => void;
  /**
   * PLAT-10266 (Card 3) — replace the fragment's translation manually: open the
   * shipped type-replacement span editor on the fragment (DESIGN §7 "Replace
   * manually"). Optional: the row renders only when the host wires it, so a
   * host that offers re-translate alone (or a future variant) shows just that.
   */
  onReplaceManually?: () => void;
  /** Dismiss the menu (click-away, or after an action fires). */
  onClose: () => void;
}

export const StaleFragmentResolveMenu: React.FC<
  StaleFragmentResolveMenuProps
> = ({ anchorElement, onReTranslate, onReplaceManually, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  useOutsideAlerter(ref, onClose);

  // PLAT-10266 (Card 8) — native-level Escape trap. The Translation Review
  // installs a window-level, capture-phase Escape handler that closes the WHOLE
  // review; this menu opens on right-click, which leaves focus on the document
  // body (it suppresses mousedown so it never steals focus), so a listener bound
  // only to the menu wrapper would never see the Escape. Listen on the window in
  // the capture phase instead: Escape closes only this menu and stops the event
  // so background shortcuts don't fire while it is open. Mirrors the
  // `CoveragePopover` trap (`CoveragePopover.tsx:246-267`). `onClose` identity
  // isn't stable (the host passes an inline arrow), so read the latest via a ref.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      e.stopPropagation();
    };
    const stop = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return;
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', stop, true);
    window.addEventListener('keypress', stop, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', stop, true);
      window.removeEventListener('keypress', stop, true);
    };
  }, []);

  // Placement computed once on mount from the anchor's rect (the fragment is
  // stable while the menu shows). `position: fixed` ⇒ viewport coordinates.
  // Height grows with the optional manual row so the above/below flip clears it.
  const [placement] = useState(() =>
    computePlacement(
      anchorElement,
      MENU_WIDTH,
      ROW_HEIGHT * (onReplaceManually ? 2 : 1),
    ),
  );

  return (
    <Surface
      ref={ref}
      placement={placement}
      // Suppress mousedown so the right-click selection isn't collapsed before
      // the click handler runs (mirrors SpanReplaceTrigger / the pill menus).
      onMouseDown={e => e.preventDefault()}
      onMouseUp={e => e.stopPropagation()}
      data-testid="stale-fragment-resolve-menu"
      role="menu"
    >
      <ContextMenuItem
        type="button"
        role="menuitem"
        onClick={e => {
          e.stopPropagation();
          onReTranslate();
          onClose();
        }}
        data-testid="stale-resolve-retranslate"
        aria-label="Re-translate this fragment from the machine"
      >
        <RefreshIcon strokeColor="currentColor" />
        Re-translate (machine)
      </ContextMenuItem>
      {onReplaceManually && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          onClick={e => {
            e.stopPropagation();
            onReplaceManually();
            onClose();
          }}
          data-testid="stale-resolve-manual"
          aria-label="Replace this fragment's translation manually"
        >
          <ReplaceTermIcon strokeColor="currentColor" />
          Replace manually
        </ContextMenuItem>
      )}
    </Surface>
  );
};

function computePlacement(
  anchor: HTMLElement,
  width: number,
  height: number,
): { left: number; top: number } {
  const rect = anchor.getBoundingClientRect();
  const MARGIN = 6;
  // Prefer above the fragment; drop below when there's no room above.
  const openBelow = rect.top < height + MARGIN;
  const top = openBelow ? rect.bottom + MARGIN : rect.top - height - MARGIN;
  const clamped = clampMenuToViewport(rect.left, top, width, height);
  return { left: clamped.x, top: clamped.y };
}

const Surface = styled(ContextMenuSurface).attrs(
  (props: { placement: { left: number; top: number } }) => props,
)`
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
`;
